import { useMutation, useQuery } from "@tanstack/react-query";
import { Formik, useFormikContext } from "formik";

import { getNotifications, updateNotifications } from "src/api/settings";
import { Button } from "src/components/ui/button";
import { Switch } from "src/components/ui/switch";
import { SettingsPanel } from "src/components/common";
import { NOTIFICATIONS_SETTINGS } from "src/lib/constants";
import { showSuccessToast } from "src/lib/utils";

interface NotificationSetting {
	name: string;
	value: string;
	description?: string;
}

type NotificationValues = Record<string, boolean>;

const Notifications = () => {
	const { data, refetch } = useQuery({ queryKey: ["notification-settings"], queryFn: getNotifications, suspense: true });

	const { mutate, isLoading } = useMutation(updateNotifications, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Notification settings updated");
			refetch();
		},
	});

	const initialValues = (data?.data ?? {}) as NotificationValues;
	const groups = Object.entries(NOTIFICATIONS_SETTINGS as Record<string, NotificationSetting[]>);

	return (
		<Formik initialValues={initialValues} enableReinitialize onSubmit={(values) => mutate(values)}>
			{({ values, dirty, handleSubmit }) => (
				<form onSubmit={handleSubmit}>
					<SettingsPanel
						title="Notification Settings"
						description="Choose which alerts you receive and how."
						actions={
							<Button size="sm" type="submit" disabled={!dirty || isLoading} className="tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								{isLoading ? "Saving..." : "Save updates"}
							</Button>
						}
					>
						<div className="tw-space-y-6">
							{groups.map(([groupName, settings]) => (
								<div key={groupName}>
									<p className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">
										{groupName}
									</p>
									<div className="tw-divide-y tw-divide-border tw-rounded-[10px] tw-border tw-border-border">
										{settings.map((setting) => (
											<NotificationRow key={setting.value} setting={setting} value={Boolean(values[setting.value])} />
										))}
									</div>
								</div>
							))}
						</div>
					</SettingsPanel>
				</form>
			)}
		</Formik>
	);
};

const NotificationRow = ({ setting, value }: { setting: NotificationSetting; value: boolean }) => {
	const { setFieldValue } = useFormikContext<NotificationValues>();

	return (
		<div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-3.5 tw-py-3">
			<div className="tw-min-w-0">
				<p className="tw-text-[13.5px] tw-font-semibold tw-capitalize tw-text-text">{setting.name.split("_").join(" ")}</p>
				{setting.description && <p className="tw-text-[12.5px] tw-text-text-soft">{setting.description}</p>}
			</div>
			<Switch checked={value} onCheckedChange={(next) => setFieldValue(setting.value, next)} />
		</div>
	);
};

export default Notifications;
